// src/components/challenges/UserTimelineModal.tsx

import React from 'react';
import styled from 'styled-components';
import { UserTimelineData } from './challenge.types.';
import { RiPinDistanceFill } from "react-icons/ri";
import StepsIcon from "../assets/StepsIcon.png";
import CaloriesIcon from "../assets/CaloriesIcon.png";
import { SlFire } from "react-icons/sl";
import { IoFootsteps } from "react-icons/io5";
import { FaMapMarkerAlt } from "react-icons/fa";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: var(--page-color1);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 95vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #094752 0%, #14b8a6 100%);
  padding: 16px 20px; /* reduce vertical padding */
  color: white;
  position: relative;
  margin:10px;
  border-radius:20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px; /* was 24px */
`;


const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const HeaderTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const UserAvatar = styled.div<{ image?: string }>`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${props => props.image ? `url(${props.image})` : 'rgba(255, 255, 255, 0.2)'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h3`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 600;
  color: white;
`;

const UserStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActiveIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px #10b981;
`;

const ModalBody = styled.div`
  padding: 5px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatCard = styled.div`
  background: var(--page-color);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatIcon = styled.div<{ hasImage?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.hasImage ? 'transparent' : 'linear-gradient(135deg, #0d9488, #14b8a6)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius:40px;
  }

  svg {
    color: #0d9488;
    font-size: 28px;
  }
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0d9488;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const TimelineSection = styled.div``;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color);
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineItem = styled.div<{ isCompleted: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 16px 0;
  border-left: 2px solid ${props => props.isCompleted ? '#0d9488' : '#e2e8f0'};
  padding-left: 24px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 20px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.isCompleted ? '#0d9488' : '#94a3b8'};
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineDate = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 4px;
`;

const TimelineDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TimelineValue = styled.div<{ isCompleted: boolean }>`
  font-size: 14px;
  color: #64748b;
`;

const TimelineStatus = styled.div<{ isCompleted: boolean }>`
  font-size: 14px;
  color: ${props => props.isCompleted ? '#0d9488' : '#94a3b8'};
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
`;
const TimelineScrollArea = styled.div`
  flex: 1;
  min-height: 200px;
  max-height: calc(80vh - 400px); /* adjusts dynamically */
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
`;


interface UserTimelineModalProps {
  user: UserTimelineData;
  metricType: 'steps' | 'distance' | 'caloriesburned';
  onClose: () => void;
  challengeUnit?: string;
}

const UserTimelineModal: React.FC<UserTimelineModalProps> = ({ user, metricType, onClose, challengeUnit = 'count' }) => {
  const getMetricIconComponent = () => {
    switch (metricType) {
      case 'steps':
        return <IoFootsteps />;
      case 'distance':
        return <FaMapMarkerAlt />;
      case 'caloriesburned':
        return <SlFire />;
      default:
        return '📊';
    }
  };

  const hasImageIcon = () => {
    return metricType === 'distance';
  };

  const getMetricUnit = () => {
    console.log('challengeUnit received:', challengeUnit); // ADD THIS

    if (challengeUnit && challengeUnit.trim() !== '') {
      if (challengeUnit.toLowerCase() === 'count') {
        return 'Steps';
      }
      return challengeUnit;
    }
    return 'Count';
  };
  const formatValue = (value: number) => {
    // if (metricType === 'distance') {
    //   return ().toFixed(1);
    // }
    return value.toLocaleString();
  };

  const getTotalLabel = () => {
    switch (metricType) {
      case 'steps': return 'Total Steps';
      case 'distance': return 'Total Distance';
      case 'caloriesburned': return 'Total Calories';
      default: return 'Total Points';
    }
  };

  const getunit = () => {
    switch (metricType) {
      case 'distance': return 'meters';
      case 'caloriesburned': return 'kcal';
      case 'steps': return 'steps';
      default: return '';
    }
  }

  const getaverageUnit = () => {
    switch (metricType) {
      case 'distance': return 'distance';
      case 'caloriesburned': return 'caloriesburned';
      case 'steps': return 'steps';
      default: return '';
    }
  }

  const getLastActiveText = () => {
    if (user.active_days === 0) {
      return 'N/A';
    }

    if (!user.daily_progress || user.daily_progress.length === 0) {
      return 'N/A';
    }

    const sortedProgress = [...user.daily_progress].sort((a, b) => {
      const dateA = new Date(a.date.split(' ').reverse().join('-'));
      const dateB = new Date(b.date.split(' ').reverse().join('-'));
      return dateB.getTime() - dateA.getTime();
    });

    const latestEntry = sortedProgress[0];
    if (!latestEntry) return 'N/A';

    const [day, month, year] = latestEntry.date.split(' ');
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const lastActiveDate = new Date(parseInt(year), monthMap[month], parseInt(day));

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastActive = new Date(lastActiveDate);
    lastActive.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - lastActive.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      const dd = String(lastActiveDate.getDate()).padStart(2, '0');
      const mm = String(lastActiveDate.getMonth() + 1).padStart(2, '0');
      const yyyy = lastActiveDate.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
  };

  const calculateAverageSteps = () => {
    if (!user.daily_progress || user.daily_progress.length === 0) {
      return 0;
    }
    const total = user.daily_progress.reduce((sum, day) => sum + day.value, 0);
    return Math.round(total / user.daily_progress.length * 100) / 100;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <CloseButton onClick={onClose}>×</CloseButton>

          <UserProfile>
            <UserAvatar>
              {user.image_url && user.image_url.trim() !== "" ? (
                <img
                  src={user.image_url}
                  alt={user.user_name}
                  style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                user.user_name.split(" ").map(n => n.charAt(0)).join("").substring(0, 2)
              )}
            </UserAvatar>

            <UserInfo>
              <UserName>{user.user_name}</UserName>
              <UserStats>
                <StatBadge>
                  <span>{getTotalLabel()}: {formatValue(user.total_steps)}{" "}{getunit()}</span>
                </StatBadge>
                <StatBadge>
                  <ActiveIndicator />
                  <span>Active days {user.active_days}</span>
                </StatBadge>
              </UserStats>
            </UserInfo>
          </UserProfile>
        </ModalHeader>

        <ModalBody>
          <StatsGrid>
            <StatCard>
              <StatIcon hasImage={hasImageIcon()}>
                {getMetricIconComponent()}
              </StatIcon>
              <StatValue>{calculateAverageSteps().toFixed(2)}</StatValue>
              <StatLabel>Average {getaverageUnit()}</StatLabel>
            </StatCard>

            <StatCard>
              <StatIcon>🕐</StatIcon>
              <StatValue>{getLastActiveText()}</StatValue>
              <StatLabel>Last Active</StatLabel>
            </StatCard>
          </StatsGrid>

          <TimelineSection>
            <SectionTitle>{getMetricUnit()} data</SectionTitle>

            <TimelineScrollArea>
              {user.daily_progress && user.daily_progress.length > 0 ? (
                <TimelineList>
                  {user.daily_progress.map((day, index) => (
                    <TimelineItem key={index} isCompleted={day.status === 'completed'}>
                      <TimelineContent>
                        <TimelineDate>{day.date}</TimelineDate>
                        <TimelineDetails>
                          <TimelineValue isCompleted={day.status === 'completed'}>
                            {formatValue(day.value)}{" "}{getunit()}
                          </TimelineValue>
                          <TimelineStatus isCompleted={day.status === 'completed'}>
                            Status: {day.status}
                          </TimelineStatus>
                        </TimelineDetails>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </TimelineList>
              ) : (
                <EmptyState>
                  <EmptyIcon>📊</EmptyIcon>
                  <EmptyText>No daily progress data available</EmptyText>
                </EmptyState>
              )}
            </TimelineScrollArea>
          </TimelineSection>

        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default UserTimelineModal;